import { useState, useEffect, useRef } from 'react';
import { motion } from 'motion/react';
import { Play, Pause } from 'lucide-react';
import { LikeButton } from './ui/LikeButton';
import { ArrowLeft, Heart, MessageCircle, Share2, MoreHorizontal, Megaphone, FileEdit, Trash2, Ban, Send, MapPin, Lock, Square, Mic, X, Image as ImageIcon, Download } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { ScrollArea } from './ui/scroll-area';
import { FlagImage } from './utils/countryFlags';
import { FriendBadge } from './ui/FriendBadge';
import { LikersModal } from './ui/LikersModal';
import { getYapInteractionId, getYapPostType, isQuoteShout } from './utils/yapUtils';
import { useYapInteractions } from './YapInteractionsContext';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from './ui/dropdown-menu';
import { ShareShoutModal } from './ShareShoutModal';
import { EmbeddedYap } from './EmbeddedYap';
import { ImageWithPreview } from './ImageWithPreview';
import { toast } from 'sonner';
import { useUserData } from './UserDataContext';
import { useContentFilter } from '../hooks/useContentFilter';
import { formatTimeAgo, formatFirestoreTime } from './utils/timeFormat';
import { ParallaxBackground } from './utils/parallax';
import { YapService, UserService } from '../services/api';
import { StorageService } from '../services/StorageService';
import { FormattedText } from './FormattedText';
import { VoiceReplyBubble } from './VoiceReplyBubble';
import { LinkPreview } from './LinkPreview';
import { extractFirstUrl } from './utils/textParsers';
import { VoiceRecorderUI } from './VoiceRecorderUI';
import { useVoiceRecorder } from '../hooks/useVoiceRecorder';
import { reportToWatchers } from '../services/WatcherService';
import { getUserOriginFlag } from '../utils/userUtils';
import { useScreenReady } from '../contexts/ScreenTransitionContext';
import { PostReplies } from './PostReplies';
import { PostMenu } from './ui/PostMenu';

interface ShoutDetailsScreenProps {
  shout: any;
  onBack: () => void;
  onDeleteShout?: () => void;
  onDeleteCaption?: () => void;
}

interface Reply {
  id: string;
  authorId: string | number;
  author: string;
  username: string;
  avatar: string;
  content: string;
  time: string;
  likes: number;
  likedBy: (string | number)[];
  audioUrl?: string;
  duration?: number;
  type?: 'text' | 'voice';
  image?: string;
}

export function ShoutDetailsScreen({ shout, onBack, onDeleteShout, onDeleteCaption }: ShoutDetailsScreenProps) {
  const [showReplyBox, setShowReplyBox] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  // The shout's likers list, loaded when opened. null = closed.
  const [likers, setLikers] = useState<{ title: string; load: () => Promise<string[]> } | null>(null);
  const { isYapLiked, isPostLiked, getPostLikeCount, handleInteraction, adjustReplyCount, getPostReplyCount, getLikeCount, getShoutCount } = useYapInteractions();
  const { userProfile, removeShout, updateShoutCaption, isTribeMember, isForumMember, isFriend, blockUser } = useUserData();
  const { isBlockedContent, isHiddenContent } = useContentFilter();
  // A shared link or a push tap lands here with an id-only stub, so the screen
  // has nothing to render until the refresh below returns. Arriving in-app
  // carries the full shout in router state and is ready immediately.
  const [isLoadingShout, setIsLoadingShout] = useState(
    !!(shout && Object.keys(shout).length <= 2 && shout.id),
  );

  useScreenReady(!isLoadingShout);

  // 🔒 FRESH DATA SYNC: Refresh shout and original yap data on mount
  const [displayShout, setDisplayShout] = useState(shout);
  const [shouterProfile, setShouterProfile] = useState<any>(null);
  const [originalYapDeleted, setOriginalYapDeleted] = useState(false);

  // Group Interaction Gate
  const isGroupInteractionAllowed =
    (!displayShout.tribeId || isTribeMember(displayShout.tribeId)) &&
    (!displayShout.forumId || isForumMember(displayShout.forumId));

  // Which post the reply counter is keyed to. The displayed count already used
  // these helpers, but the increment/decrement calls passed the raw shout id.
  // For a simple repost those are different documents — getYapInteractionId
  // resolves to the ORIGINAL yap — so posting a reply moved a counter nobody was
  // reading and the number on screen never changed.
  const replyCountId = getYapInteractionId(displayShout);
  const replyCountType = getYapPostType(displayShout);

  useEffect(() => {
    if (!shout?.id) return;

    // Reset displayShout when shout prop changes
    setDisplayShout(shout);
    
    const refreshShoutData = async () => {
      try {
        const freshShout = await YapService.fetchYapById(shout.id);
        if (freshShout) {
          const originalId = (freshShout as any).originalYapId || (freshShout as any).originalYap?.id;
          if (originalId) {
            const freshOriginal = await YapService.fetchYapById(originalId);
            if (freshOriginal) {
              freshShout.originalYap = freshOriginal;
              setOriginalYapDeleted(false);
            } else {
              setOriginalYapDeleted(true);
            }
          }
          setDisplayShout(freshShout);

          // 🏠 RESIDENCY SYNC: Fetch the shouter's live profile to get current residency
          const shouterId = freshShout.userId || freshShout.authorId || (freshShout as any).shoutedBy;
          if (shouterId) {
            const profile = await UserService.fetchUserById(String(shouterId));
            if (profile) setShouterProfile(profile);
          }

          window.dispatchEvent(new CustomEvent('refreshInteractions', { detail: { force: true } }));
        }
      } catch (error) {
        console.error('[ShoutDetailsScreen] Error refreshing shout data:', error);
      } finally {
        setIsLoadingShout(false);
      }
    };

    refreshShoutData();
  }, [shout?.id]);


  const handleReportShout = async () => {
    if (!userProfile?.id) return;
    const reason = window.prompt("Why are you reporting this shout? (Harassment, Spam, etc.)");
    if (!reason || reason.trim().length < 4) {
      if (reason !== null) toast.error("A reason is required to report.");
      return;
    }

    try {
      const shouterUsername = displayShout.shoutedByUsername || 'anonymous';
      await reportToWatchers(
        String(userProfile.id),
        shout.id,
        'shout',
        reason.trim(),
        {
          shouterUsername,
          shouterId: displayShout.shoutedBy,
          caption: displayShout.shoutCaption
        }
      );
      toast.success("Report submitted to Watchers.");
    } catch (err) {
      toast.error("Failed to submit report.");
    }
  };

  const handleDeleteCaption = () => {
    if (displayShout.originalYap?.id) {
      updateShoutCaption(displayShout.originalYap.id, undefined, undefined);
      toast.success('Caption and images removed');
      if (onDeleteCaption) onDeleteCaption();
    }
  };

  const handleDeleteShout = async () => {
    if (displayShout.originalYap?.id) {
      await removeShout(displayShout.originalYap.id);
      toast.success('Shout deleted');
      if (onDeleteShout) onDeleteShout();
    }
  };

  // Profile navigation, matching YapDetailScreen's author-click behaviour.
  //
  // The shouter's avatar previously dispatched a bare navigateToScreen:'profile',
  // which always opened the VIEWER's own profile no matter whose shout it was.
  // navigateToFriendProfile is the correct event — AppRouter's handler already
  // redirects to your own profile when the id matches you, so no branch is needed
  // here.
  const goToProfile = (user: { id?: any; name?: string; username?: string; image?: string }) => {
    if (!user?.id) return;
    window.dispatchEvent(new CustomEvent('navigateToFriendProfile', {
      detail: {
        id: String(user.id),
        name: user.name || '',
        username: (user.username || '').replace(/^@/, ''),
        image: user.image || '',
      },
    }));
  };

  const goToShouter = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    goToProfile({
      id: displayShout.shoutedBy,
      name: shouterName,
      username: shouterUsername,
      image: shouterAvatar,
    });
  };

  // The embedded original yap's author — previously unreachable from this screen
  // entirely, since EmbeddedYap rendered the handle as plain text.
  const goToOriginalAuthor = () => {
    goToProfile({
      id: originalYapData?.userId || originalYapData?.authorId,
      name: originalAuthor,
      username: originalUsername,
      image: originalAvatar,
    });
  };

  const formatRelativeTime = (timestamp: any) => {
    return formatFirestoreTime(timestamp);
  };

  // 🚫 A shout by (or quoting) a blocked author is treated exactly like a missing
  // one, matching YapDetailScreen. Replies were already filtered below, but the
  // shout itself was not, so a deep link or notification still rendered it in
  // full. Copy is deliberately identical to the not-found case rather than
  // naming the block.
  if (!shout || isHiddenContent(displayShout)) {
    return (
      <div className="w-full h-screen bg-light-pattern dark:bg-[#0D0D0D] flex items-center justify-center relative">
        <ParallaxBackground speed={0.12} />
        <div className="text-center relative z-10">
          <p className="text-[#666] dark:text-[#E5E5E5] text-sm text-center">Shout not found</p>
          <Button onClick={onBack} className="bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] hover:opacity-90 text-white">
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const hasCaption = displayShout.shoutCaption || displayShout.shoutImages?.length > 0;
  // Shouter identity comes from the shout document only. These used to fall back
  // to `userProfile`, so any shout missing its denormalized shouter fields (or
  // still loading, e.g. opened from a link) rendered as though the VIEWER had
  // shouted it — wrong attribution, and it made the author tap ambiguous.
  // Prefer the freshly fetched profile when we have it, otherwise the snapshot.
  const shouterName = shouterProfile?.name || displayShout.shoutedByName || 'Anonymous';
  const shouterAvatar = shouterProfile?.avatar || displayShout.shoutedByAvatar || '';
  const shouterUsername = shouterProfile?.username || displayShout.shoutedByUsername || 'anonymous';
  // The flag belongs to the SHOUTER, like the name and avatar beside it.
  //
  // This previously read `authorCountryFlag || userCountry` off the shout. On a
  // shout those two keys disagree about whose flag they hold: the feed's mapper
  // spreads the original yap first (so `userCountry` is the ORIGINAL author's)
  // and then overwrites `authorCountryFlag` with the shouter's. Whenever the
  // shouter had no flag recorded, the fallback quietly showed the original
  // author's flag instead — and it then swapped once refreshShoutData replaced
  // the object with the raw shout document. That swap is the flag "changing"
  // shortly after the screen opens.
  const shouterFlag = shouterProfile
    ? getUserOriginFlag(shouterProfile)
    : (displayShout.shoutedByCountryFlag || displayShout.authorCountryFlag || '');
  const shoutTime = displayShout.timestamp || displayShout.time || 'Just now';
  const formattedShoutTime = formatRelativeTime(shoutTime);

  const originalYapData = displayShout.originalYap || {};
  const originalYapId = (displayShout as any).originalYapId || originalYapData.id;
  const originalAuthor = originalYapData.userName || originalYapData.author || 'Unknown User';
  const originalAvatar = originalYapData.userAvatar || originalYapData.avatar || '';
  const originalUsername = originalYapData.username || originalYapData.userUsername || '@unknown';
  const originalAuthorCountryFlag = originalYapData.authorCountryFlag || originalYapData.country || '';
  const originalContent = originalYapData.content || '';
  const originalTime = originalYapData.timestamp || 'Just now';
  const originalCountry = originalYapData.userCountry || originalYapData.country || '';
  const originalVisibility = originalYapData.friendsOnly ? 'friends' : 'public';
  const originalYapLikes = originalYapData.likes || 0;
  const originalYapComments = originalYapData.comments || 0;
  const formattedOriginalTime = formatRelativeTime(originalTime);

  // Download and See Likes, matching YapDetailScreen.
  //
  // A simple repost owns nothing of its own: its likes are the original yap's
  // likes (getYapInteractionId points there) and the only content on screen is
  // the original's. A quote shout owns both its likes and its caption.
  const isQuote = isQuoteShout(displayShout);
  // Same rule as Yap Details: only the person the likes belong to sees who
  // liked. On a simple repost that is the original author, not the reposter.
  const likesOwnerId = isQuote ? displayShout.shoutedBy : (originalYapData.userId || originalYapData.authorId);
  const canSeeLikes = !!userProfile?.id && !!likesOwnerId && String(likesOwnerId) === String(userProfile.id);

  const openLikers = () => {
    setLikers(isQuote
      ? { title: 'Liked your shout', load: () => YapService.getShoutLikers(String(shout.id), (userProfile as any)?.ageGroup) }
      : { title: 'Liked your yap', load: () => YapService.getYapLikers(String(originalYapId), (userProfile as any)?.ageGroup) });
  };

  const handleDownload = async () => {
    const { downloadYapAsImage } = await import('../utils/downloadYap');
    const deps = { userProfile, getLikeCount, getShoutCount, getPostReplyCount };
    // The image card has no embedded-yap layout, so a caption-less repost
    // downloads as the yap it reposts rather than as an empty card.
    if (!isQuote && originalYapData.id) {
      await downloadYapAsImage({ ...originalYapData, time: formattedOriginalTime }, deps);
      return;
    }
    await downloadYapAsImage({ ...displayShout, time: formattedShoutTime }, deps);
  };

  return (
    // 100dvh, not h-screen alone: on iOS 100vh is taller than the visible
    // area, which pushed the bottom of the scroll area off screen.
    <div className="w-full h-screen relative bg-light-pattern dark:bg-[#0D0D0D] overflow-hidden" style={{ height: '100dvh' }}>
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <ParallaxBackground speed={0.05} />
      </div>
      {/* No pb-20 here. It reserved an 80px strip below the scroll area, so the
          content ended well above the bottom of the screen and the replies
          looked cut off. Yap Details has never had it. */}
      <div className="w-full max-w-2xl mx-auto h-full flex flex-col relative z-10">
        <div
          className="pb-4 px-4 border-b border-black/10 dark:border-white/10 bg-[#F5F5DC]/80 dark:bg-[#121212] backdrop-blur-sm flex items-center justify-between"
          style={{ paddingTop: 'max(60px, calc(env(safe-area-inset-top) + 32px))' }}
        >
          <div className="flex items-center gap-4">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={onBack}
              className="text-black dark:text-[#E5E5E5] hover:text-[#F39C12] dark:hover:text-white"
            >
              <ArrowLeft className="w-6 h-6" />
            </motion.button>
            <h1 className="text-2xl text-black dark:text-white">Shout Details</h1>
          </div>
          {canSeeLikes && (
            <Button
              variant="ghost"
              size="sm"
              onClick={openLikers}
              className="h-8 px-2 text-xs font-medium text-black/60 dark:text-white/60 hover:text-[#E74C3C]"
            >
              See Likes
            </Button>
          )}
        </div>

        {/* minHeight: 0 is what makes this scroll. A flex item's min-height
            defaults to auto, so flex-1 alone let the scroll area grow to its
            content instead of being capped by the column: the viewport and its
            content were the same height, nothing ever overflowed, and the part
            below the fold was simply unreachable.

            Inline rather than the min-h-0 class: src/index.css is committed
            precompiled Tailwind and does not contain that utility, so the class
            silently does nothing. */}
        <ScrollArea className="flex-1" style={{ minHeight: 0 }}>
          {/* Bottom padding clears the home indicator and the bottom nav, the
              same allowance YapDetailScreen makes. Inline because the
              precompiled index.css may not carry the arbitrary-value class. */}
          <div className="p-4 overflow-x-hidden" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 120px)' }}>
            <div className="mb-6 p-4 rounded-2xl bg-[#F5F5F5] dark:bg-[#1a1a1a] border border-black/10 dark:border-white/10">
              <div className="flex items-start gap-3 mb-2">
                <div className="flex flex-col items-center gap-1.5 shrink-0">
                  <Avatar
                    className="w-12 h-12 border-2 border-[#4478ea] cursor-pointer"
                    onClick={goToShouter}
                  >
                    <AvatarImage src={shouterAvatar} />
                    <AvatarFallback>{shouterName[0]}</AvatarFallback>
                  </Avatar>
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-[#4478ea] dark:text-[#4478ea]">
                    <Megaphone className="w-3.5 h-3.5 fill-[#4478ea] dark:fill-[#4478ea]" />
                    <span>Shouted</span>
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-1.5 min-h-[24px] cursor-pointer w-fit" onClick={goToShouter}>
                    <h4 className="font-bold text-black dark:text-white text-lg leading-tight">{shouterName}</h4>
                    {shouterFlag && (
                      // Sized exactly as YapDetailScreen and the world feed do it.
                      // This used to be `w-4 h-3` with no object-contain, which
                      // forced every flag into a 4:3 box and visibly squashed the
                      // ones that aren't (Nepal, Switzerland, and any w80 asset
                      // whose ratio differs), so the same flag looked different
                      // here than on a yap.
                      <FlagImage
                        countryName={shouterFlag}
                        size="w80"
                        className="w-4 aspect-3/2 object-contain rounded shrink-0"
                      />
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-[#666666] dark:text-[#a0a0a0] mt-0.5">
                    <span className="truncate cursor-pointer" onClick={goToShouter}>{shouterUsername.startsWith('@') ? shouterUsername : `@${shouterUsername}`}</span>
                    <span className="text-[10px] opacity-40 shrink-0">•</span>
                    <span className="whitespace-nowrap shrink-0">{formattedShoutTime}</span>
                  </div>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    {shout.friendsOnly && (
                      <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-[#E74C3C]/20 dark:bg-[#DC2626]/30 text-[#E74C3C] dark:text-[#DC2626]">
                        <Lock className="w-3 h-3" />
                        Friends Only
                      </span>
                    )}
                  </div>
                </div>
                {/* The location used to run off the edge of the card. This row
                    was shrink-0 and the text whitespace-nowrap with no maximum
                    width, so a long residence ("Port Harcourt, Rivers State,
                    Nigeria") made the row wider than its parent and, because
                    nothing was allowed to compress, it overflowed rather than
                    fitting. The icon and the menu keep their size; only the text
                    gives way, and it ellipsises instead of wrapping so the row
                    stays one line tall.

                    Truncation is inline rather than the `truncate` class:
                    src/index.css is committed pre-compiled Tailwind, so a class
                    that is not already in that bundle renders nothing. */}
                <div
                  className="flex items-center gap-1.5 ml-auto h-full self-start pt-1"
                  style={{ minWidth: 0, flexShrink: 1 }}
                >
                  <MapPin style={{ width: '10px', height: '10px' }} className="text-[#4478ea] shrink-0" />
                  <span
                    className="font-normal text-[#666666] dark:text-[#a0a0a0]"
                    style={{
                      fontSize: '10px',
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {(() => {
                      const currentProfile = shouterProfile || userProfile;
                      const res = currentProfile?.home_residence || currentProfile?.location || shout.home_residence || shout.shouterResidence || shout.location;
                      if (!res || res.trim().length <= 1 || res === shout.authorCountry || res === (displayShout as any).userCountry || res === 'Earth') return null;
                      return res;
                    })()}
                  </span>
                  {/* Shared menu. This surface had the thinnest hand-written
                      list of all of them — no Block, no Copy Link, no View
                      Profile, and no Edit even though shout captions are
                      editable. It now offers the same set as everywhere else,
                      plus the shout-only caption removal. */}
                  <PostMenu
                    post={displayShout as any}
                    surface="detail"
                    collectionName="shouts"
                    onReport={handleReportShout}
                    onDeleteRequested={handleDeleteShout}
                    onRemoveShoutExtras={handleDeleteCaption}
                    onSeeLikes={() => setLikers({
                      title: 'Liked your shout',
                      load: () => YapService.getShoutLikers(String(shout.id), (userProfile as any)?.ageGroup),
                    })}
                    onChanged={() => window.dispatchEvent(new CustomEvent('yapUpdated'))}
                  />
                </div>
              </div>

              {displayShout.shoutCaption && (
                <>
                  <FormattedText text={displayShout.shoutCaption} className="text-[#333333] dark:text-[#E5E5E5] mb-2 leading-relaxed text-[17px] font-medium whitespace-pre-wrap wrap-break-word block" />
                  {extractFirstUrl(displayShout.shoutCaption) && (
                    <div className="mb-2">
                      <LinkPreview url={extractFirstUrl(displayShout.shoutCaption)!} />
                    </div>
                  )}
                </>
              )}
              {displayShout.shoutImages && displayShout.shoutImages.length > 0 && (
                <div className={`mb-3 ${displayShout.shoutImages.length >= 2 ? 'grid grid-cols-2' : 'flex justify-start'}`}>
                  {displayShout.shoutImages.map((img: string, idx: number) => (
                    <div key={idx} className="rounded-2xl overflow-hidden border border-black/5 dark:border-white/10 bg-black/5 dark:bg-white/5 w-fit">
                      <ImageWithPreview src={img} alt={`Shout image ${idx + 1}`} className="object-contain" style={{ maxHeight: '400px', maxWidth: '100%', width: 'auto', height: 'auto' }} />
                    </div>
                  ))}
                </div>
              )}
              {displayShout.audioUrl && (
                <div className="mb-4">
                  <VoiceReplyBubble audioUrl={displayShout.audioUrl} duration={displayShout.duration} className="w-full" />
                </div>
              )}

              <EmbeddedYap
                suppressLinkPreview={!!extractFirstUrl(displayShout.shoutCaption)}
                yapId={originalYapId}
                username={originalUsername}
                authorName={originalAuthor}
                avatar={originalAvatar}
                timestamp={formattedOriginalTime}
                content={originalContent}
                images={originalYapData.images || (originalYapData.image ? [originalYapData.image] : [])}
                image={originalYapData.image}
                country={originalCountry}
                authorCountryFlag={originalAuthorCountryFlag}
                showLocation={true}
                likes={originalYapLikes}
                comments={originalYapComments}
                shouts={originalYapData.shouts || 0}
                isLiked={isYapLiked(originalYapId)}
                isShout={true}
                isDeleted={originalYapDeleted}
                minimal={!hasCaption}
                showActions={false}
                onAuthorClick={goToOriginalAuthor}
                onYapClick={() => {
                  const originalYap = {
                    ...originalYapData,
                    id: originalYapData.id || shout.id + '-original',
                    author: originalAuthor,
                    username: originalUsername,
                    avatar: originalAvatar,
                    content: originalContent,
                    country: originalCountry,
                    visibility: originalVisibility,
                    time: originalTime,
                  };
                  window.dispatchEvent(new CustomEvent('navigateToYapDetail', { detail: originalYap }));
                }}
                className="mb-4"
              />

              <div className="flex items-center gap-3 mb-6 px-1">
                <LikeButton
                  postId={getYapInteractionId(displayShout)}
                  postType={getYapPostType(displayShout)}
                  originalLikeCount={displayShout.likes || 0}
                  canInteract={() => isGroupInteractionAllowed}
                  onBlockedInteraction={() => {
                    toast.error("Members only", { description: `Join this ${displayShout.tribeId ? 'tribe' : 'forum'} to like yaps.` });
                  }}
                  className="px-2 py-1"
                  iconSize={20}
                />

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (!isGroupInteractionAllowed) {
                      toast.error("Members only", { description: `Join this ${displayShout.tribeId ? 'tribe' : 'forum'} to reply.` });
                      return;
                    }
                    setShowReplyBox(!showReplyBox);
                  }}
                  className={`flex items-center gap-1.5 h-auto px-2 py-1 ${!isGroupInteractionAllowed ? 'opacity-50' : 'text-[#666666] dark:text-[#E5E5E5] hover:text-[#F39C12] dark:hover:text-[#4478ea]'}`}
                >
                  <MessageCircle className="w-5 h-5" />
                  <span className="text-sm">{getPostReplyCount(displayShout.comments || 0, replyCountId, replyCountType)}</span>
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setShowShareModal(true)} className="flex items-center gap-1.5 h-auto px-2 py-1 text-[#666666] dark:text-[#E5E5E5] hover:text-[#F39C12] dark:hover:text-[#4478ea]">
                  <Share2 className="w-5 h-5" />
                </Button>
              </div>
            </div>

            <PostReplies
              postId={String(shout.id)}
              collectionName="shouts"
              countId={replyCountId}
              countType={replyCountType}
              opAuthorId={displayShout.shoutedBy}
              postOwnerId={displayShout.shoutedBy}
              canInteract={isGroupInteractionAllowed}
              showReplyBox={showReplyBox}
              onShowReplyBoxChange={setShowReplyBox}
              onRepliesChanged={() => window.dispatchEvent(new CustomEvent('yapUpdated'))}
            />
          </div>
        </ScrollArea>
        <LikersModal
          open={!!likers}
          onClose={() => setLikers(null)}
          loadUserIds={likers?.load}
          title={likers?.title}
        />
        {showShareModal && (
          <ShareShoutModal isOpen={showShareModal} shout={shout} onClose={() => setShowShareModal(false)} />
        )}
      </div>
    </div>
  );
}
